import { useCallback, useContext, useSyncExternalStore } from "react";

import {
  EMPTY_PROPERTY_FOREST_SNAPSHOT,
  PropertyForestSnapshotContext,
  type PropertyForestSnapshot,
} from "./PropertyForestSnapshotContextState";
import {
  readPropertyForestSnapshot,
  subscribePropertyForestSnapshot,
} from "@/shared/config/propertyForestSnapshotStore";

/** Presentation-only lookup: never loads, mutates, or fabricates map data. */
export function usePropertyForestSnapshot(
  propertyId: string,
): PropertyForestSnapshot {
  const contextSnapshot = useContext(PropertyForestSnapshotContext)[propertyId];
  const subscribe = useCallback(
    (listener: () => void) =>
      subscribePropertyForestSnapshot(propertyId, listener),
    [propertyId],
  );
  const getSnapshot = useCallback(
    () => readPropertyForestSnapshot(propertyId),
    [propertyId],
  );
  const publishedSnapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getSnapshot,
  );
  return (
    publishedSnapshot ??
    contextSnapshot ??
    EMPTY_PROPERTY_FOREST_SNAPSHOT
  );
}

/** True once a route has published (or provided) map data for the property. */
export function usePropertyForestSnapshotPublished(propertyId: string): boolean {
  return usePropertyForestSnapshot(propertyId) !== EMPTY_PROPERTY_FOREST_SNAPSHOT;
}
